import type { ResolveValuePermutations } from '../type/common/types';
const PLACEHOLDER_START = '${';
const PLACEHOLDER_END = '}';

/**
 * Resolves a variable name against resolvedData.
 *
 * - Single segment (`answer`): searches the flat `values` map of every alias.
 * - Two segments (`alias.subKey`): selects the alias in the permutation, then
 *   looks up `subKey` inside its `values` map.
 */
const lookupVariable = <Types,>(variableName: string, resolvedData: ResolveValuePermutations<Types>): string | undefined => {
  const dotIndex = variableName.indexOf('.');
  for (const permutation of resolvedData) {
    if (dotIndex === -1) {
      // Flat lookup — search values of every alias
      for (const {
        values
      } of Object.values(permutation)) {
        if (variableName in values) {
          const val = values[variableName];
          return Array.isArray(val) ? val.join(', ') : val ? val.toString() : undefined;
        }
      }
    } else {
      // Nested lookup — first part is the alias, second part is the sub-key
      const alias = variableName.slice(0, dotIndex);
      const subKey = variableName.slice(dotIndex + 1);
      const entry = permutation[alias];
      if (entry !== undefined && subKey in entry.values) {
        const val = entry.values[subKey];
        return Array.isArray(val) ? val.join(', ') : val ? val.toString() : undefined;
      }
    }
  }
  return undefined;
};

/**
 * Replaces `${variable_name}` tokens in an error message with values from
 * resolvedData. Variable names are matched against the sub-alias keys inside
 * each permutation's `values` map. When a value is a string array it is
 * joined with ", ". Unmatched tokens are left as-is.
 */
export const interpolateErrorMessage = <Types,>(errorMessage: string, resolvedData: ResolveValuePermutations<Types>): string => {
  const parts = errorMessage.split(PLACEHOLDER_START);
  if (parts.length === 1) {
    return errorMessage;
  }
  let result = parts[0] ?? '';
  for (const [i, part] of parts.entries()) {
    if (i === 0) {
      continue;
    }
    const closingIndex = part.indexOf(PLACEHOLDER_END);
    if (closingIndex === -1) {
      // No closing brace — treat as literal, restore the opening token
      result += PLACEHOLDER_START + part;
      continue;
    }
    const variableName = part.slice(0, closingIndex);
    const after = part.slice(closingIndex + 1);
    const resolved = lookupVariable(variableName, resolvedData);
    result += resolved !== undefined ? resolved + after : PLACEHOLDER_START + part;
  }
  return result;
};